<template>
    <div class="chart-wrapper">
        <!-- Loading skeleton -->
        <div v-if="loading || hideChart" class="h-96 bg-card border border-border rounded-lg shadow-sm p-4">
            <!-- Chart title skeleton -->
            <div class="flex items-center flex-col w-full">
                <Skeleton class="h-5 w-48 mb-2" />
                <!-- Chart subtitle skeleton -->
                <Skeleton class="h-4 w-32 mb-6" />
            </div>

            <!-- Chart area skeleton - horizontal axis bars -->
            <div class="flex items-end justify-center space-x-1 sm:space-x-2 h-60 mt-8">
                <!-- Vertical bars extending up from horizontal axis - responsive count -->
                <div class="flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-24" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-40" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-32" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-44" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-36" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-42" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-30" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <!-- Hide some bars on smaller screens -->
                <div class="hidden sm:flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-46" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="hidden sm:flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-38" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="hidden md:flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-34" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
                <div class="hidden md:flex flex-col items-center space-y-1">
                    <Skeleton class="w-6 sm:w-8 h-41" />
                    <Skeleton class="h-2 sm:h-3 w-6 sm:w-8" />
                </div>
            </div>
        </div>

        <!-- Chart display -->
        <Chart v-else-if="chartOptionsExists" ref="chart" :options="chartOptionsToUse" :update-args="[true, false]" />

        <!-- No data state -->
        <div v-else class="h-96 bg-card border border-border rounded-lg shadow-sm p-4">
            <div class="flex flex-col items-center justify-center p-8 text-center h-full">
                <div class="p-3 mx-auto text-primary bg-accent-foreground/30 rounded-full">
                    <BarChart class="h-7 w-7" />
                </div>
                <p class="mt-4 text-muted-foreground">No data to display</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Chart } from 'highcharts-vue';
import HighCharts from 'highcharts';
import { BarChart } from 'lucide-vue-next';
import Skeleton from './ui/skeleton/Skeleton.vue';

import 'highcharts/modules/map';
import 'highcharts/modules/data';
import 'highcharts/highcharts-more';

import RoundedCorners from 'highcharts-rounded-corners';
import { applyShadcnTheme } from '../utils/highcharts-shadcn-theme';
import { formatter } from '../utils/utils';
import moment from 'moment-timezone';
import _ from 'lodash';

RoundedCorners(HighCharts);

// Apply shadcn theme first
applyShadcnTheme();

// Then set timezone (won't override theme colors)
HighCharts.setOptions({
    time: {
        timezone: moment.tz.guess()
    },
    accessibility: {
        enabled: false
    },
    chart: {
        // Set will-read-frequently for better performance
        events: {
            load: function () {
                if (this.container) {
                    const canvases = this.container.querySelectorAll('canvas');
                    canvases.forEach((canvas) => {
                        //  const ctx =
                        canvas.getContext('2d', { willReadFrequently: true });
                    });
                }
            }
        }
    }
});

const props = defineProps({
    title: { type: String, default: null },
    subtitle: { type: String, default: null },
    xTitle: { type: String, default: null },
    yTitle: { type: String, default: null },
    series: { type: Array, default: () => [] },
    chartOptions: { type: Object, default: null },
    loading: { type: Boolean, default: false }
});

const chart = ref(null);
const hideChart = ref(false);

const chartOptionsExists = computed(() => {
    return !_.isEmpty(props.chartOptions) && !_.isEmpty(props.chartOptions?.series);
});

const chartOptionsToUse = computed(() => {
    return (
        props.chartOptions || {
            chart: {
                type: 'column'
            },
            title: {
                text: props.title
            },
            subtitle: {
                text: props.subtitle
            },
            xAxis: {
                categories: [props.xTitle],
                crosshair: true
            },
            yAxis: {
                min: 0,
                title: {
                    text: props.yTitle
                }
            },
            tooltip: {
                headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
                pointFormatter: function () {
                    var value;
                    if (this.y % 1 !== 0) {
                        value = HighCharts.numberFormat(this.y, 2, '.', '');
                    } else {
                        value = HighCharts.numberFormat(this.y, 0, '', '');
                    }
                    return (
                        '<tr><td style="color:' +
                        this.series.color +
                        ';padding:0">' +
                        this.series.name +
                        ': </td>' +
                        '<td style="padding:0"><b>' +
                        formatter(value).display +
                        '</b></td></tr>'
                    );
                },
                footerFormat: '</table>',
                shared: true,
                useHTML: true
            },
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0
                }
            },
            series: props.series || []
        }
    );
});

watch(
    () => props.chartOptions,
    (newValue, oldValue) => {
        if (newValue?.series?.length !== oldValue?.series?.length) {
            hideChart.value = true;
            setTimeout(() => {
                hideChart.value = false;
            }, 1);
        } else {
            chart.value?.chart?.update(newValue);
            chart.value?.chart?.redraw();
        }
    },
    { deep: true }
);
</script>
